import { FetchError } from "../../lib/client/client"
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { productsQueryKeys } from "./products"

const COLLECTION_QUERY_KEY = "collections" as const
export const collectionsQueryKeys = queryKeysFactory(COLLECTION_QUERY_KEY)

// Types for Collections (adapted from Range structure)
export interface Collection {
  id: string
  title: string
  handle: string
  products?: Array<{
    id: string
    title: string
  }>
  created_at: string
  updated_at: string
  image?: string
}

export interface CreateCollectionRequest {
  title: string
  handle?: string
  image?: string
}

export interface UpdateCollectionRequest {
  title?: string
  handle?: string
  image?: string
}

export interface UpdateCollectionProductsRequest {
  add?: string[]
  remove?: string[]
}

export const useCollection = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      { collection: Collection },
      FetchError,
      Collection,
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.detail(id),
    queryFn: async () => {
      try {
        const response = await sdk.admin.productCollection.retrieve(id)
        return response
      } catch (error) {
        console.error('Error fetching collection:', error)
        throw error
      }
    },
    select: (data) => data.collection,
    ...options,
  })

  return { collection: data, ...rest }
}

export const useCollections = (
  query?: {
    new?: boolean
    sort?: 'desc' | 'asc'
    published?: boolean
  },
  options?: Omit<
    UseQueryOptions<
      { collections: Collection[]; count: number; offset: number; limit: number },
      FetchError,
      { collections: Collection[]; count: number },
      QueryKey
    >,
    "queryFn" | "queryKey"
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: collectionsQueryKeys.list(query),
    queryFn: async () => {
      try {
        const response = await sdk.admin.productCollection.list(query)
        return response
      } catch (error) {
        console.error('Error fetching collections:', error)
        throw error
      }
    },
    ...options,
  })

  return { 
    collections: data?.collections ?? [], 
    count: data?.count ?? 0,
    ...rest 
  }
}

export const useUpdateCollection = (
  id: string,
  options?: UseMutationOptions<
    { collection: Collection },
    FetchError,
    UpdateCollectionRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.productCollection.update(id, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useUpdateCollectionProducts = (
  id: string,
  options?: UseMutationOptions<
    { collection: Collection },
    FetchError,
    UpdateCollectionProductsRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.productCollection.updateProducts(id, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })
      /**
       * Invalidate products list query to ensure that the products collections are updated.
       */
      queryClient.invalidateQueries({
        queryKey: productsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useCreateCollection = (
  options?: UseMutationOptions<
    { collection: Collection },
    FetchError,
    CreateCollectionRequest
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.productCollection.create(payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKeys.lists() })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useDeleteCollection = (
  id: string,
  options?: UseMutationOptions<
    {},
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () => sdk.admin.productCollection.delete(id),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: collectionsQueryKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: collectionsQueryKeys.detail(id),
      })

      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
